import React, { useEffect, useState } from 'react';
import './JobStatusTableComponent.scss';
import { useNavigate } from 'react-router-dom';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { fetchJobDetails } from 'actions/homePage';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { breadcrumbActions } from 'actions/breadcrumb';
import { buildBreadCrumbData } from 'utils/utils';
import { Table } from '@tfecom-thermofisher/react-komodo-design';
import Loading from 'components/Placeholders/Loading';
import { JOB_STATUS_LABEL_MAPPING, JOB_STATUS_MAPPING, REFRESH_BUTTON_TITLE } from 'constants/constants';
import classNames from 'classnames';
import { CorePagination, CoreSearch } from '@komodo-design-system/komodo-components-react';
import { BASE_URL } from 'constants/routes';

const JobStatusTableComponent = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [pageNumber, setPageNumber] = useState(0);
  const [sortFilter, setSortFilter] = useState({
    sortBy: {
      id: '',
      dir: '',
      isSort: false
    },
    filters: {}
  });
  const [searchInput, setSearchInput] = useState('');
  const [minText, setMinText] = useState(true);
  const {userName} = useSelector((state) => state.userTypeReducer?.userType?.data) || {};
  const jobDetailsReducerData = useSelector(
    (state) => state.homePage.fetchedJobDetailsData,
    shallowEqual
  );

  const [filteredResults, setFilteredResults] = useState([]);

  useEffect(() => {
    setFilteredResults(jobDetailsReducerData || []);
  }, [jobDetailsReducerData]);

  /*Search Functionality */
  const searchItems = (searchValue) => {
    setSearchInput(searchValue);
    if (!searchValue) {
      setFilteredResults(jobDetailsReducerData);
      setMinText(true);
    } else if (searchValue.length > 2) {
      const filteredRows = jobDetailsReducerData?.list?.filter((key) => {        
        return (
          key?.jobName?.toLowerCase().includes(searchValue.toLowerCase()) ||
          key?.fileName?.toLowerCase().includes(searchValue.toLowerCase()) ||
          key?.status?.toLowerCase().includes(searchValue.toLowerCase()) ||
          key?.bulkUploadId
            ?.toString()
            ?.toLowerCase()
            .includes(searchValue.toLowerCase())
        );
      });
      setFilteredResults({
        list: filteredRows,
        totalCount: filteredRows.length
      });
      setMinText(false);
    }
  };

  const onConfigChange = (dat) => {
    if (dat.sortBy.id !== '') {
      const dir = dat.sortBy.dir;      
      dispatch(fetchJobDetails(userName, 0, dir?.toUpperCase(), dat.sortBy.id));
      setPageNumber(0);
      setSortFilter({
        sortBy: dat.sortBy,
        filters: dat.filters
      });
    }
    else {
      dispatch(fetchJobDetails(userName, 0, 'DESC', 'uploadedDate'));
      setPageNumber(0);
      setSortFilter({
        sortBy: dat?.sortBy,
        filters: dat?.filters
      });
    }
  };

  const cellDataSorting = (tableData) => {
    const column = tableData?.column;
    const defaultSortable = {
      active: false,
      options: []
    };

    const sortable =
      column.sortable === true
        ? {
          ...defaultSortable,
          active: true
        }
        : { ...defaultSortable, ...column.sortable };
    return sortable;
  };

  const columns = [
    {
      Header: 'Job Name',
      accessor: 'jobName',
      sortable: true,
      Cell: (tableData) => {
        const sortable = cellDataSorting(tableData);
        const index = tableData?.row?.index;
        const cellData = tableData?.data[index];
        const disabledLinkClass =
          cellData.status?.toLowerCase() === 'created' ? 'disable-link' : '';        
        return (
          <span
            style={{ width: '25%' }}
            className={classNames({
              sortable: sortable.active && !sortable.options.length,
              certificateType: sortable.active
            })}>
            <span
              className={`jobNameLink ${disabledLinkClass}`}
              onClick={() => tableDetailData(cellData?.bulkUploadId)}>
              {cellData.jobName ? cellData.jobName : '-'}
            </span>
          </span>
        );
      }
    },
    {
      Header: 'Bulk Job ID',
      accessor: 'bulkUploadId',
      sortable: true,
      Cell: (tableData) => {
        const sortable = cellDataSorting(tableData);
        const index = tableData?.row?.index;
        const cellData = tableData?.data[index];
        return (
          <span
            style={{ width: '25%' }}
            className={classNames({
              sortable: sortable.active && !sortable.options.length,
              certificateType: sortable.active
            })}>
            {cellData.bulkUploadId}
          </span>
        );
      }
    },
    {
      Header: 'Source File',
      accessor: 'fileName',
      sortable: true,
      Cell: (tableData) => {
        const sortable = cellDataSorting(tableData);
        const index = tableData?.row?.index;
        const cellData = tableData?.data[index];
        return (
          <span
            className={classNames({
              sortable: sortable.active && !sortable.options.length
            })}>
            {cellData.fileName}
          </span>
        );
      }
    },
    {
      Header: 'Start Date',
      accessor: 'uploadedDate',
      sortable: true,
      Cell: (tableData) => {
        const sortable = cellDataSorting(tableData);
        const index = tableData?.row?.index;
        const cellData = tableData?.data[index];
        return (
          <span
            className={classNames({
              sortable: sortable.active && !sortable.options.length
            })}>
            {cellData?.uploadedDate
              ? dayjs(cellData?.uploadedDate.split('T')[0]).format('MM-DD-YYYY')
              : '-'}
          </span>
        );
      }
    },
    {
      Header: 'End Date',
      accessor: 'completedDate',
      sortable: true,
      Cell: (tableData) => {
        const sortable = cellDataSorting(tableData);
        const index = tableData?.row?.index;
        const cellData = tableData?.data[index];
        return (
          <span
            className={classNames({
              sortable: sortable.active && !sortable.options.length
            })}>
            {cellData?.completedDate
              ? dayjs(cellData?.completedDate.split('T')[0]).format(
                'MM-DD-YYYY'
              )
              : '-'}
          </span>
        );
      }
    },
    {
      Header: 'Status',
      accessor: 'status',
      sortable: true,
      Cell: (tableData) => {
        const sortable = cellDataSorting(tableData);
        const index = tableData?.row?.index;
        const cellData = tableData?.data[index];
        const isStatusLabel = JOB_STATUS_LABEL_MAPPING[cellData.status?.toLowerCase()];
        return (
          <span
            className={classNames({
              sortable: sortable.active && !sortable.options.length
            })}>
            <span className={`status-label ${isStatusLabel}`}>
              {JOB_STATUS_MAPPING[cellData.status.toLowerCase()]}
            </span>
          </span>
        );
      }
    },
    {
      Header: 'No of Assets',
      accessor: 'assetCount',
      sortable: true,
      Cell: (tableData) => {
        const sortable = cellDataSorting(tableData);
        const index = tableData?.row?.index;
        const cellData = tableData?.data[index];
        return (
          <span
            className={classNames({
              sortable: sortable.active && !sortable.options.length
            })}>
            {cellData.assetCount}
          </span>
        );
      }
    }
  ];


  useEffect(() => {
    dispatch(fetchJobDetails(userName, 0, 'DESC', 'uploadedDate'));
  }, [dispatch, userName]);

  const handlePagination = (event) => {
    const pgNumber = event.detail.activePage;
    if (pgNumber - 1 === pageNumber) {
      return;
    }
    setPageNumber(pgNumber - 1);
    dispatch(fetchJobDetails(userName, pgNumber - 1, sortFilter.sortBy.dir?.toUpperCase(), sortFilter.sortBy.id));
  };

  const tableDetailData = (id) => {
    navigate(BASE_URL+'/job-status-detail-view/' + id);
    window.scrollTo(0, 0);
    const breadCrumbData = buildBreadCrumbData('/job-status-detail-view/');
    dispatch(breadcrumbActions.breadcrumb(breadCrumbData));
  };

  const onRefreshClick = async () => {
    dispatch(fetchJobDetails(userName, 0, 'DESC', 'uploadedDate'));
    setPageNumber(0);
    setSearchInput('');
  };  
  return (
    <section className="blk-jobstatus-tablecomponent">
      <div className="table-title">
        <h2>
          <FormattedMessage id="jobStatus" />
        </h2>
        {jobDetailsReducerData?.list?.length > 0 && (
          <span
            className="icon-24-refresh-bold"
            onClick={onRefreshClick}
            title={REFRESH_BUTTON_TITLE}></span>
        )}
      </div>
      <div className="search_block">
        <CoreSearch
          id='coreSearch'
          style={{ display: 'inline-block', width: '300px' }}
          placeholder="Search"
          value={searchInput}
          onSearchCleared={() => searchItems('')}
          onSearchQueryChanged={(e) => searchItems(e.detail)}
        />
        {minText && (
          <span className="indication_text">
            Please enter alteast 3 characters.
          </span>
        )}
        {filteredResults?.list?.length === 0 && searchInput.length > 0 && (
          <span className="errorMsg">Sorry, no matching record found</span>
        )}
      </div>
      <div>
        {filteredResults?.list?.length > 0 ? (
          <Table
            columns={columns}
            data={filteredResults?.list || []}
            onConfigChange={onConfigChange}
            size="normal"
            sortFilter={sortFilter}
          />
        ) : (
          <div className="noData">
            <p className="blk-jobstatus-tablecomponent__nodata">
              <FormattedMessage id="noDatatoShow" />
            </p>
          </div>
        )}
        {filteredResults?.totalCount > 10 ? (
          <CorePagination
            totalresults={filteredResults?.totalCount}
            variants='{"dropdown": false, "indicator": false, "goto": false}'
            resultsperpage={[10]}
            onActivePageChange={handlePagination}
            activepage={pageNumber + 1}
          ></CorePagination>
        ) : null}
      </div>
    </section>
  );
};

JobStatusTableComponent.propTypes = {
  isLoggedIn: PropTypes.bool
};

export default JobStatusTableComponent;
